import React, {
  useState,
  useEffect,
  useReducer,
  useCallback,
  useContext,
} from "react";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import ScheduleModal from "../../components/ScheduleModal/StepperScheduleModal";
import ModernConfirmationModal from "../../components/ModernConfirmationModal";
import toastError from "../../errors/toastError";
import { successToast, errorToast } from "../../utils/customToast";
import moment from "moment";
import { AuthContext } from "../../context/Auth/AuthContext";
import usePlans from "../../hooks/usePlans";
import "moment/locale/pt-br";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";

import "./Schedules.css";

// Defina a função getUrlParam antes de usá-la
function getUrlParam(paramName) {
  const searchParams = new URLSearchParams(window.location.search);
  return searchParams.get(paramName);
}

const reducer = (state, action) => {
  if (action.type === "LOAD_SCHEDULES") {
    const schedules = action.payload;
    const newSchedules = [];

    schedules.forEach((schedule) => {
      const scheduleIndex = state.findIndex((s) => s.id === schedule.id);
      if (scheduleIndex !== -1) {
        state[scheduleIndex] = schedule;
      } else {
        newSchedules.push(schedule);
      }
    });

    return [...state, ...newSchedules];
  }

  if (action.type === "UPDATE_SCHEDULES") {
    const schedule = action.payload;
    const scheduleIndex = state.findIndex((s) => s.id === schedule.id);

    if (scheduleIndex !== -1) {
      state[scheduleIndex] = schedule;
      return [...state];
    } else {
      return [schedule, ...state];
    }
  }

  if (action.type === "DELETE_SCHEDULE") {
    const scheduleId = action.payload;

    const scheduleIndex = state.findIndex((s) => s.id === scheduleId);
    if (scheduleIndex !== -1) {
      state.splice(scheduleIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: "24px",
    overflowY: "auto",
    ...theme.scrollbarStyles,
    background: "#f5f7fa",
  },
  container: {
    padding: "24px",
    background: "#f5f7fa",
    minHeight: "100vh",
  },
  header: {
    marginBottom: "24px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "16px",
  },
  mainTitle: {
    fontSize: "24px",
    fontWeight: 700,
    color: "#1a1a1a",
    margin: 0,
  },
  subtitle: {
    fontSize: "14px",
    color: "#666",
    marginTop: "4px",
  },
  addButton: {
    padding: "10px 20px",
    borderRadius: "6px",
    fontSize: "14px",
    fontWeight: 600,
    textTransform: "none",
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    transition: "all 0.3s ease",
    "&:hover": {
      transform: "translateY(-1px)",
      boxShadow: "0 2px 8px rgba(59, 130, 246, 0.3)",
    },
  },
  calendarContainer: {
    marginTop: theme.spacing(3),
  },
  calendarHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    borderBottom: "1px solid #e8ecf1",
  },
  monthTitle: {
    fontSize: "18px",
    fontWeight: 600,
    color: "#1a1a1a",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  navigationButtons: {
    display: "flex",
    gap: theme.spacing(1),
  },
  navButton: {
    minWidth: "40px",
    width: "40px",
    height: "40px",
    padding: 0,
    borderRadius: "6px",
    backgroundColor: "#e8ecf1",
    color: "#1a1a1a",
    transition: "all 0.3s ease",
    border: "none",
    cursor: "pointer",
    "&:hover": {
      backgroundColor: theme.palette.primary.main,
      color: "#fff",
    },
  },
  filterTabs: {
    display: "flex",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
    flexWrap: "wrap",
  },
  filterTab: {
    padding: "8px 16px",
    borderRadius: "20px",
    border: "1px solid #e8ecf1",
    backgroundColor: "transparent",
    color: "#666",
    fontSize: "14px",
    fontWeight: 500,
    cursor: "pointer",
    transition: "all 0.3s ease",
    "&:hover": {
      borderColor: theme.palette.primary.main,
      color: theme.palette.primary.main,
    },
  },
  filterTabActive: {
    backgroundColor: theme.palette.primary.main,
    color: "#fff",
    borderColor: theme.palette.primary.main,
  },
  calendarGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(7, 1fr)",
    gap: "1px",
    backgroundColor: "#e8ecf1",
    padding: "1px",
    borderRadius: "8px",
    overflow: "hidden",
    border: "1px solid #e8ecf1",
  },
  dayHeader: {
    padding: "12px",
    backgroundColor: "#f9fafb",
    textAlign: "center",
    fontSize: "12px",
    fontWeight: 600,
    color: "#666",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  dayCell: {
    minHeight: "100px",
    padding: "8px",
    backgroundColor: "#fff",
    position: "relative",
    cursor: "pointer",
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: "#f9fafb",
    },
  },
  dayNumber: {
    fontSize: "14px",
    fontWeight: 600,
    color: "#1a1a1a",
    marginBottom: "4px",
  },
  eventItem: {
    fontSize: "11px",
    padding: "6px 8px",
    borderRadius: "6px",
    backgroundColor: theme.palette.primary.main,
    color: "#fff",
    marginBottom: "4px",
    cursor: "pointer",
    transition: "all 0.2s ease",
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
      "& $eventActions": {
        opacity: 1,
      },
    },
  },
  eventText: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    flex: 1,
    marginRight: "4px",
  },
  eventActions: {
    opacity: 0,
    display: "flex",
    gap: "2px",
    transition: "opacity 0.2s ease",
  },
  eventActionButton: {
    padding: "2px",
    minWidth: "auto",
    width: "20px",
    height: "20px",
    borderRadius: "3px",
    backgroundColor: "rgba(255, 255, 255, 0.2)",
    color: "#fff",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.3)",
    },
    "& .MuiSvgIcon-root": {
      fontSize: "12px",
    },
  },
}));

const Schedules = () => {
  const classes = useStyles();
  const history = useHistory();

  const { user, socket } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [deletingSchedule, setDeletingSchedule] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [schedules, dispatch] = useReducer(reducer, []);
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [contactId, setContactId] = useState(+getUrlParam("contactId"));
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);

  const { getPlanCompany } = usePlans();

  useEffect(() => {
    async function fetchData() {
      const companyId = user.companyId;
      const planConfigs = await getPlanCompany(undefined, companyId);
      if (!planConfigs.plan.useSchedules) {
        errorToast(i18n.t("schedules.noPermission"));
        setTimeout(() => {
          history.push(`/`);
        }, 500); // Reduzir delay de 1000ms para 500ms
      }
    }
    fetchData();
  }, [user, history, getPlanCompany]);

  const fetchSchedules = useCallback(async () => {
    try {
      const { data } = await api.get("/schedules", {
        params: { searchParam, pageNumber },
      });

      dispatch({ type: "LOAD_SCHEDULES", payload: data.schedules });
      setHasMore(data.hasMore);
      setLoading(false);
    } catch (err) {
      toastError(err);
    }
  }, [searchParam, pageNumber]);

  const handleOpenScheduleModalFromContactId = useCallback(() => {
    if (contactId) {
      handleOpenScheduleModal();
    }
  }, [contactId]);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      fetchSchedules();
    }, 200); // Reduzir delay de 500ms para 200ms
    return () => clearTimeout(delayDebounceFn);
  }, [
    searchParam,
    pageNumber,
    contactId,
    fetchSchedules,
    handleOpenScheduleModalFromContactId,
  ]);

  useEffect(() => {
    // handleOpenScheduleModalFromContactId();
    // const socket = socketManager.GetSocket(user.companyId, user.id);

    const onCompanySchedule = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_SCHEDULES", payload: data.schedule });
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_SCHEDULE", payload: +data.scheduleId });
      }
    };

    socket.on(`company${user.companyId}-schedule`, onCompanySchedule);

    return () => {
      socket.off(`company${user.companyId}-schedule`, onCompanySchedule);
    };
  }, [socket]);

  const cleanContact = () => {
    setContactId("");
  };

  const handleOpenScheduleModal = () => {
    setSelectedSchedule(null);
    setScheduleModalOpen(true);
  };

  const handleCloseScheduleModal = () => {
    setSelectedSchedule(null);
    setScheduleModalOpen(false);
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleEditSchedule = (schedule) => {
    setSelectedSchedule(schedule);
    setScheduleModalOpen(true);
  };

  const handleDeleteSchedule = async (scheduleId) => {
    try {
      await api.delete(`/schedules/${scheduleId}`);
      successToast(i18n.t("schedules.deleteSuccess"));
    } catch (err) {
      toastError(err);
    }
    setDeletingSchedule(null);
    setConfirmModalOpen(false);
    setSearchParam("");
    setPageNumber(1);

    dispatch({ type: "RESET" });
    setPageNumber(1);
    await fetchSchedules();
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const truncate = (str, len) => {
    if (!str || typeof str !== "string") return "";
    if (str.length > len) {
      return str.substring(0, len) + "...";
    }
    return str;
  };

  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const getEventsForDay = (day) => {
    return schedules.filter((schedule) => {
      const scheduleDate = new Date(schedule.sendAt);
      return (
        scheduleDate.getDate() === day &&
        scheduleDate.getMonth() === currentDate.getMonth() &&
        scheduleDate.getFullYear() === currentDate.getFullYear()
      );
    });
  };

  const handlePrevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
  };

  const handleDayClick = (day) => {
    const clickedDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
    setSelectedDate(clickedDate);
    setSelectedSchedule(null);
    setScheduleModalOpen(true);
  };

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(currentDate);
    const firstDay = getFirstDayOfMonth(currentDate);
    const days = [];
    const dayNames = [
      i18n.t("schedules.dayNames.sunday"),
      i18n.t("schedules.dayNames.monday"),
      i18n.t("schedules.dayNames.tuesday"),
      i18n.t("schedules.dayNames.wednesday"),
      i18n.t("schedules.dayNames.thursday"),
      i18n.t("schedules.dayNames.friday"),
      i18n.t("schedules.dayNames.saturday"),
    ];

    // Render day headers
    dayNames.forEach((day) => {
      days.push(
        <div key={`header-${day}`} className={classes.dayHeader}>
          {day}
        </div>
      );
    });

    // Render empty cells for days before month starts
    for (let i = 0; i < firstDay; i++) {
      days.push(
        <div key={`empty-${i}`} className={classes.dayCell} style={{ backgroundColor: "#fafafa" }} />
      );
    }

    // Render days of month
    for (let day = 1; day <= daysInMonth; day++) {
      const dayEvents = getEventsForDay(day);
      days.push(
        <div 
          key={`day-${day}`} 
          className={classes.dayCell}
          onClick={() => handleDayClick(day)}
        >
          <div className={classes.dayNumber}>{day}</div>
          {dayEvents.map((event) => (
            <div
              key={event.id}
              className={classes.eventItem}
              title={event.contact?.name}
            >
              <div 
                className={classes.eventText}
                onClick={(e) => {
                  e.stopPropagation();
                  handleEditSchedule(event);
                }}
              >
                {truncate(event.contact?.name, 12)}
              </div>
              <div className={classes.eventActions}>
                <Button
                  className={classes.eventActionButton}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEditSchedule(event);
                  }}
                  title={i18n.t("schedules.edit")}
                >
                  <EditIcon />
                </Button>
                <Button
                  className={classes.eventActionButton}
                  onClick={(e) => {
                    e.stopPropagation();
                    setDeletingSchedule(event);
                    setConfirmModalOpen(true);
                  }}
                  title={i18n.t("schedules.delete")}
                >
                  <DeleteOutlineIcon />
                </Button>
              </div>
            </div>
          ))}
        </div>
      );
    }

    return <>{days}</>;
  };

  return (
    <MainContainer>
      <ModernConfirmationModal
        title={i18n.t("schedules.deleteTitle")}
        message={
          deletingSchedule
            ? i18n.t("schedules.deleteConfirmNamed", { name: deletingSchedule.contact?.name })
            : i18n.t("schedules.deleteConfirm")
        }
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={() => handleDeleteSchedule(deletingSchedule.id)}
        type="delete"
        confirmText={i18n.t("schedules.confirmDelete")}
        cancelText={i18n.t("schedules.cancel")}
      />
      {scheduleModalOpen && (
        <ScheduleModal
          open={scheduleModalOpen}
          onClose={handleCloseScheduleModal}
          reload={fetchSchedules}
          scheduleId={selectedSchedule ? selectedSchedule.id : null}
          contactId={contactId}
          cleanContact={cleanContact}
          user={user}
          selectedDate={selectedDate}
        />
      )}
      <MainHeader>
        <div className={classes.header}>
          <div>
            <h1 className={classes.mainTitle}>📅 {i18n.t("schedules.title")}</h1>
          </div>
        </div>
      </MainHeader>
      <Paper className={classes.mainPaper} variant="outlined">


        <div className={classes.calendarContainer}>
          <div className={classes.calendarHeader}>
            <h2 className={classes.monthTitle}>
              {currentDate.toLocaleDateString(i18n.language === "es" ? "es-ES" : i18n.language === "en" ? "en-US" : "pt-BR", { month: "long", year: "numeric" }).toUpperCase()}
            </h2>
            <div className={classes.navigationButtons}>
              <Button
                className={classes.navButton}
                onClick={handlePrevMonth}
              >
                <ChevronLeftIcon />
              </Button>
              <Button
                className={classes.navButton}
                onClick={handleNextMonth}
              >
                <ChevronRightIcon />
              </Button>
            </div>
          </div>

          <div className={classes.calendarGrid}>
            {renderCalendar()}
          </div>
        </div>
      </Paper>
    </MainContainer>
  );
};

export default Schedules;
